import css from "./standart.module.css";
import { logOut } from "../../lib/logOut";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { fetchCSRFToken, getCSRFToken } from "../../lib/csrf";
import SubModal from "./SubModal";
import { pollSubscriptionStatus } from "../../lib/subscriptionStatusApi";

const Header = ({ authUser, pageTheme }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [isLightMode, setIsLightMode] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubModalOpen, setIsSubModalOpen] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsProfileOpen(false);
      }
    };
    if (isProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileOpen]);

  const languages = [
    { code: "en", label: "EN" },
    { code: "uk", label: "UA" },
    { code: "pl", label: "PL" },
  ];

  useEffect(() => {
   if(pageTheme === 'dark') {
    setIsLightMode(false);
   } else {
    setIsLightMode(true);
   }
  }, [])

  // Scroll detection for header shadow effect
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch current subscription when user is authenticated
  const fetchCurrentSubscription = async () => {
    if (!authUser?.isAuthenticated) return;

    let activeSubscription = null;

    // 1) Try orders endpoint first (fast)
    try {
      const response = await axios.get("/api/billing/orders/", {
        withCredentials: true,
      });

      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        activeSubscription = response.data.find((sub) => Number(sub.status) === 2) || null;
      }
    } catch (error) {
      console.error("Error fetching current subscription (orders):", error);
    }

    if (activeSubscription) {
      setCurrentSubscription(activeSubscription);
      return;
    }

    // 2) Fallback to subscription/status polling endpoint (authoritative)
    try {
      const result = await pollSubscriptionStatus(5, 2000);
      if (result && typeof result === "object" && Number(result.status) === 2) {
        setCurrentSubscription(result);
      } else {
        setCurrentSubscription(result);
      }
    } catch (error) {
      console.error("Error fetching current subscription (poll):", error);
      setCurrentSubscription(null);
    }
  };

  useEffect(() => {
    fetchCurrentSubscription();
  }, [authUser?.isAuthenticated]);

  const isHomePage = location.pathname === '/';

  const isNavLinkActive = (path) => location.pathname === path;

  const getNavLinkClass = (path) =>
    `${css.headerNavLink}${isNavLinkActive(path) ? ` ${css.headerNavActiveLink}` : ''}`;

  const handleNavigate = (path, options = {}) => {
    // If navigating to signup/signin from trial-lesson, add state
    const isTrialLesson = location.pathname === '/trial-lesson';
    if (isTrialLesson && (path === '/signup' || path === '/signin')) {
      options = {
        ...options,
        state: { ...(options.state || {}), fromTrialLesson: true }
      };
    }
    
    navigate(path, options);
    window.scrollTo(0, 0);
  };

  const changeLanguage = async (lang) => {
    setLoading(true);
    try {
      i18n.changeLanguage(lang);
      localStorage.setItem("selectedLanguage", lang);

      if (authUser && authUser.isAuthenticated === true) {
        let csrfToken = getCSRFToken();
        if (!csrfToken) {
          await fetchCSRFToken();
          csrfToken = getCSRFToken();
        }

        await axios.patch(
          "/api/v1/user-profile/current",
          { language: lang },
          {
            headers: {
              "Content-Type": "application/json",
              "X-CSRFToken": csrfToken,
            },
            withCredentials: true,
          }
        );
      }

      setIsOpen(false);
    } catch (error) {
      console.error("Помилка зміни мови:", error.response?.data || error);
      setIsOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const success = await logOut();
    if (success) {
      window.location.href = "/";
    }
  };

  return (
    <>
      {/* Scroll Progress Bar - only on home page */}
      {isHomePage && (
        <div className={css.scrollProgress} style={{ width: `${scrollProgress}%` }}></div>
      )}

      <header className={`${css.headerWrap} ${isScrolled ? css.scrolled : ''} ${isLightMode ? css.lightMode : ''}`}>
        <div className={css.headerContent}>
          {/* Logo */}
          <Link
            to="/"
            className={css.logo}
            onClick={(e) => {
              e.preventDefault();
              if (location.pathname === '/') {
                const videoDiscoverySection = document.querySelector('[class*="videoDiscoverySection"]');
                if (videoDiscoverySection) {
                  const headerHeight = 60;
                  const elementPosition = videoDiscoverySection.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - headerHeight - 20;
                  
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }
              } else {
                 sessionStorage.setItem("fromOnboarding", "true");
                 navigate("/", {
                  replace: true,})
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <div className={css.logoIcon}>Ai</div>
            <div className={css.logoText}>AI <em>Lingua</em></div>
          </Link>

          {/* Navigation links */}
          <nav className={css.headerNav}>
            <button
              className={getNavLinkClass('/allvideos')}
              onClick={() => handleNavigate('/allvideos')}
            >
              {t('All videos')}
            </button>
            {authUser?.isAuthenticated && (
              <button
                className={getNavLinkClass('/mylearning')}
                onClick={() => handleNavigate('/mylearning')}
              >
                {t('My learning', 'Моє навчання')}
              </button>
            )}
            <button
              className={getNavLinkClass('/pricing')}
              onClick={() => handleNavigate('/pricing')}
            >
              {t('Pricing')}
            </button>
          </nav>

          {/* Mobile menu icon */}
          <div onClick={() => setIsOpen(!isOpen)} className={`${css.menuIcon} ${isOpen ? css.active : ''}`}>
            <span></span>
          </div>

          {/* Right group */}
          <nav className={`${css.navActions} ${isOpen ? css.active : ''}`}>
            {/* Language switcher */}
            <div className={css.langSwitcher}>
              {languages.map(({ code, label }) => (
                <button
                  key={code}
                  className={`${css.langBtn} ${i18n.language === code ? css.active : ''}`}
                  onClick={() => changeLanguage(code)}
                  disabled={loading}
                >
                  {loading && i18n.language === code ? "⏳" : label}
                </button>
              ))}
            </div>

            <div className={css.authButtons}>
              {authUser && authUser.isAuthenticated === true ? (
                <div className={css.userDropdownWrap} ref={dropdownRef}>
                  <button 
                    className={css.ubtn} 
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                  >
                    <div className={css.uav}>
                      {authUser?.name ? authUser.name.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <span className={css.uname}>{authUser?.name || 'User'}</span>
                  </button>
                  
                  {isProfileOpen && (
                    <div className={css.userDropdownMenu}>
                      <button onClick={() => { setIsProfileOpen(false); handleNavigate('/profile'); }}>
                        {t("profile", "Профіль")}
                      </button>
                      <button onClick={() => { setIsProfileOpen(false); navigate('/profile', { state: { tab: 'support' } }); }}>
                        {t("Support", "Підтримка")}
                      </button>
                      <button onClick={() => { setIsProfileOpen(false); setIsSubModalOpen(true); }}>
                        {t("Subscription")}
                      </button>
                      <div className={css.userDropdownDivider}></div>
                      <button onClick={handleLogout} className={css.logoutText}>
                        {t("logout")}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <button
                    className={css.btnLogin}
                    onClick={() => handleNavigate("/signin")}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    {t("signin")}
                  </button>
                  <button
                    className={css.btnRegister}
                    onClick={() => handleNavigate("/signup")}
                  >
                    {t("register")}
                  </button>
                </>
              )}
            </div>
          </nav>
        </div>
      </header>
      
      {/* Subscription Modal */}
      <SubModal 
        isOpen={isSubModalOpen} 
        onClose={() => setIsSubModalOpen(false)} 
        authUser={authUser}
        currentSubscription={currentSubscription}
        onSubscriptionCancelled={fetchCurrentSubscription}
      />
    </>
  );
};

export default Header;
